/* eslint-disable react-hooks/exhaustive-deps */
// IosEmailComposerBody — the compose UI extracted from IosEmailComposer.
//
// This component contains all the email composition state, handlers and layout.
// It is NOT a Dialog — callers decide how to host it (inline sidebar, wrapped in
// a Dialog by IosEmailComposer, etc.).
//
// Props:
//   leadId        — lead numeric/string ID (used in the send API call)
//   templateId    — optional: pre-fills subject/body from a template on mount
//   practiceId    — practice ID (for template + send endpoints)
//   onSendSuccess — called with message ID after a successful send
//   leadDetail    — lead object (email address, name)
//   onDismiss     — called after the user cancels/discards or after a successful
//                   send (optional — defaults to no-op; Dialog wrappers pass their
//                   own close handler here)
import PropTypes from 'prop-types';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
    Box,
    Drawer,
    IconButton,
    Menu,
    MenuItem,
    Typography,
    TextField,
    CircularProgress,
    Tooltip,
    Popover,
    Chip,
    Stack,
    Divider,
    Button,
    useMediaQuery,
    InputAdornment,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import Iconify from 'src/components/Iconify';
import InlineEmailAddField from 'src/components/InlineEmailAddField';
import { callApi } from 'src/utils/apiCall';
import { dispatch } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';
import { applyStylesToTags } from 'src/utils/applyStylesToTags ';

// -----------------------------------------------
// Quill toolbar config — minimal, clean
// -----------------------------------------------
const QUILL_MODULES = {
    toolbar: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
        ['clean'],
    ],
};

const QUILL_FORMATS = ['bold', 'italic', 'underline', 'list', 'bullet', 'link'];

// -----------------------------------------------
// Hairline row — borderless iOS-style field row
// -----------------------------------------------
function FieldRow({ label, children, noDivider }) {
    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 0.75, minHeight: 44 }}>
                <Typography
                    variant="body2"
                    sx={{ fontSize: '0.82rem', color: 'text.disabled', fontWeight: 500, minWidth: 60, flexShrink: 0 }}
                >
                    {label}
                </Typography>
                <Box sx={{ flex: 1, ml: 1 }}>{children}</Box>
            </Box>
            {!noDivider && <Divider sx={{ mx: 3, borderColor: 'divider', opacity: 0.6 }} />}
        </>
    );
}
FieldRow.propTypes = { label: PropTypes.string, children: PropTypes.node, noDivider: PropTypes.bool };

// -----------------------------------------------
// BareInput — styled text input with no border/outline
// -----------------------------------------------
function BareInput({ value, onChange, placeholder, multiline, inputProps, ...rest }) {
    return (
        <TextField
            variant="standard"
            fullWidth
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            multiline={multiline}
            InputProps={{
                disableUnderline: true,
                inputProps: { style: { fontSize: '0.875rem', padding: 0 }, ...(inputProps || {}) },
            }}
            sx={{ '& .MuiInputBase-root': { p: 0 } }}
            {...rest}
        />
    );
}
BareInput.propTypes = { value: PropTypes.string, onChange: PropTypes.func, placeholder: PropTypes.string, multiline: PropTypes.bool, inputProps: PropTypes.object };

// -----------------------------------------------
// Category colour map — matches LeadTemplateMessageTableRow
// -----------------------------------------------
const CATEGORY_COLORS = {
    'follow-up':    { bg: 'rgba(255,171,0,0.10)', color: '#B76E00' },
    'follow_up':    { bg: 'rgba(255,171,0,0.10)', color: '#B76E00' },
    utility:        { bg: 'rgba(7,141,238,0.10)',  color: '#078DEE' },
    marketing:      { bg: 'rgba(133,92,248,0.10)', color: '#855CF8' },
    communication:  { bg: 'rgba(0,171,85,0.10)',   color: '#007867' },
    promotion:      { bg: 'rgba(255,171,0,0.10)',  color: '#B76E00' },
};

// -----------------------------------------------
// localStorage helpers — wrapped in try/catch for Safari private mode
// -----------------------------------------------
function getRecentTemplates(practiceId) {
    try { return JSON.parse(localStorage.getItem(`email-template-recent-${practiceId}`)) || []; }
    catch { return []; }
}

function addRecentTemplate(practiceId, templateId) {
    try {
        const prev = getRecentTemplates(practiceId);
        const next = [templateId, ...prev.filter((id) => id !== templateId)].slice(0, 5);
        localStorage.setItem(`email-template-recent-${practiceId}`, JSON.stringify(next));
    } catch { /* noop */ }
}

function getUsageCounts(practiceId) {
    try { return JSON.parse(localStorage.getItem(`email-template-usage-${practiceId}`)) || {}; }
    catch { return {}; }
}

function incrementUsageCount(practiceId, templateId) {
    try {
        const counts = getUsageCounts(practiceId);
        counts[templateId] = (counts[templateId] || 0) + 1;
        localStorage.setItem(`email-template-usage-${practiceId}`, JSON.stringify(counts));
    } catch { /* noop */ }
}

// -----------------------------------------------
// highlightMatch — wraps first occurrence of query in a <mark> span
// -----------------------------------------------
function highlightMatch(text, query) {
    if (!query || !query.trim() || !text) return text || '';
    const q = query.trim();
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text;
    return (
        <>
            {text.slice(0, idx)}
            <Box
                component="mark"
                sx={{ bgcolor: '#FFE58A', color: '#1d1d1f', borderRadius: '2px', px: '2px', py: 0 }}
            >
                {text.slice(idx, idx + q.length)}
            </Box>
            {text.slice(idx + q.length)}
        </>
    );
}

// -----------------------------------------------
// Sort option labels
// -----------------------------------------------
const SORT_LABELS = { recent: 'Recent', alphabetical: 'A–Z', 'most-used': 'Most used' };
const SORT_OPTIONS = [['recent', 'Recent'], ['alphabetical', 'A–Z'], ['most-used', 'Most used']];

// Maximum chips shown before adding the "show all" overflow button
const CAT_OVERFLOW_THRESHOLD = 8;

// -----------------------------------------------
// EmailTemplateSidePanel — self-contained left-pane template browser
// -----------------------------------------------
function EmailTemplateSidePanel({ practiceId, onSelect, selectedId }) {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [sortOrder, setSortOrder] = useState('recent');
    const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
    const [catOverflowAnchor, setCatOverflowAnchor] = useState(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    // Read once on mount — refreshes naturally when component remounts after a send
    const [recentIds] = useState(() => getRecentTemplates(practiceId));
    // Ref guard: default to "Follow up" category only once per mount
    const defaultedRef = useRef(false);
    // Ref to the chip strip scroll container for imperative scroll + scroll-state tracking
    const chipStripRef = useRef(null);

    useEffect(() => {
        if (!practiceId) return;
        setLoading(true);
        callApi(
            `/api/v1/practices/${practiceId}/engagement-templates/?ordering=-created_at&channel__in=EMAIL&limit=100&offset=0`
        )
            .then((res) => { if (res.status === 200) setTemplates(res.data?.results || []); })
            .finally(() => setLoading(false));
    }, [practiceId]);

    // Default selected category to "Follow up" (case-insensitive) the first time templates load.
    // Falls back to "All" if the practice has no follow-up category.
    useEffect(() => {
        if (defaultedRef.current || templates.length === 0) return;
        const cats = new Set(templates.map((t) => (t.category || '').trim()).filter(Boolean));
        const followUpMatch = ['Follow up', 'Follow Up', 'follow up'].find((c) => cats.has(c));
        if (followUpMatch) setSelectedCategory(followUpMatch);
        defaultedRef.current = true;
    }, [templates]);

    // Unique sorted category list; 'All' always first
    const categories = useMemo(() => {
        const cats = [...new Set(templates.map((t) => t.category).filter(Boolean))].sort();
        return ['All', ...cats];
    }, [templates]);

    // Keep left/right arrow enabled state in sync with chip strip scroll position.
    // Must be declared after `categories` so the dep array resolves correctly.
    useEffect(() => {
        const el = chipStripRef.current;
        if (!el) return undefined;
        const update = () => {
            setCanScrollLeft(el.scrollLeft > 0);
            setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
        };
        update();
        el.addEventListener('scroll', update, { passive: true });
        // ResizeObserver not available in JSDOM; guard for test environments
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
        if (ro) ro.observe(el);
        return () => {
            el.removeEventListener('scroll', update);
            if (ro) ro.disconnect();
        };
    }, [categories]);

    // True when any filter is active (category or search)
    const isFiltered = selectedCategory !== 'All' || search.trim().length > 0;

    // Filtered + sorted template list
    const displayedTemplates = useMemo(() => {
        let result = templates.filter((t) => {
            const matchesCat = selectedCategory === 'All' || t.category === selectedCategory;
            const q = search.toLowerCase().trim();
            const matchesSearch = !q ||
                t.name?.toLowerCase().includes(q) ||
                t.subject?.toLowerCase().includes(q) ||
                (t.category || '').toLowerCase().includes(q);
            return matchesCat && matchesSearch;
        });

        if (sortOrder === 'alphabetical') {
            result = [...result].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        } else if (sortOrder === 'most-used') {
            const counts = getUsageCounts(practiceId);
            result = [...result].sort((a, b) => (counts[b.id] || 0) - (counts[a.id] || 0));
        } else {
            // 'recent': items in recentIds first (by position), rest by updated_at desc
            const order = {};
            recentIds.forEach((id, i) => { order[id] = i; });
            result = [...result].sort((a, b) => {
                const ra = order[a.id] !== undefined ? order[a.id] : 999;
                const rb = order[b.id] !== undefined ? order[b.id] : 999;
                if (ra !== rb) return ra - rb;
                return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
            });
        }
        return result;
    }, [templates, selectedCategory, search, sortOrder, recentIds, practiceId]);

    // Recently used compact row — only shown when no filter is active
    const recentTemplates = useMemo(() => {
        if (isFiltered || !recentIds.length) return [];
        return recentIds.map((id) => templates.find((t) => t.id === id)).filter(Boolean);
    }, [isFiltered, recentIds, templates]);

    // Render a full-size template card
    const renderCard = (t) => {
        const catKey = (t.category || '').toLowerCase().replace(/ /g, '_');
        const catStyle = CATEGORY_COLORS[catKey] || { bg: 'rgba(145,158,171,0.10)', color: '#637381' };
        const rawPreview = t.description || t.body || '';
        const preview = rawPreview.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().slice(0, 120);
        const isSelected = selectedId === t.id;
        const q = search.trim();
        const titleText = t.subject || t.name;

        return (
            <Box
                key={t.id}
                onClick={() => onSelect(t)}
                data-testid="email-template-card"
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.25,
                    p: '10px 12px',
                    borderRadius: '10px',
                    border: '1px solid',
                    borderColor: isSelected ? 'primary.main' : 'rgba(145,158,171,0.14)',
                    bgcolor: isSelected ? (theme) => alpha(theme.palette.primary.main, 0.06) : 'background.paper',
                    cursor: 'pointer',
                    transition: 'all 0.14s ease',
                    minHeight: 84,
                    '&:hover': {
                        borderColor: 'rgba(145,158,171,0.30)',
                        bgcolor: isSelected ? undefined : (theme) => alpha(theme.palette.grey[200], 0.5),
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                    },
                    '&:active': { transform: 'scale(0.98)' },
                }}
            >
                <Box sx={{ width: 42, height: 42, borderRadius: '8px', bgcolor: 'rgba(7,141,238,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: '1px' }}>
                    <Iconify icon="eva:email-fill" width={18} sx={{ color: '#078DEE' }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'text.primary', lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {highlightMatch(titleText, q)}
                    </Typography>
                    {t.subject && t.name && t.subject !== t.name && (
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mt: '1px' }}>
                            {highlightMatch(t.name, q)}
                        </Typography>
                    )}
                    {preview && (
                        <Typography sx={{ fontSize: '12px', color: '#6e6e73', mt: '3px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: 1.45 }}>
                            {highlightMatch(preview, q)}
                        </Typography>
                    )}
                    {t.category && (
                        <Box sx={{ display: 'inline-flex', mt: '5px', px: '6px', py: '1px', borderRadius: '4px', fontSize: '10px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', bgcolor: catStyle.bg, color: catStyle.color }}>
                            {(t.category || '').replace(/_/g, ' ')}
                        </Box>
                    )}
                </Box>
            </Box>
        );
    };

    return (
        <Box
            data-testid="email-template-side-panel"
            sx={{
                width: 300,
                flexShrink: 0,
                borderRight: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                bgcolor: (theme) => alpha(theme.palette.grey[100], 0.5),
            }}
        >
            {/* ── Header: title + search row + sort ── */}
            <Box sx={{ px: 1.5, pt: 1.25, pb: 0.5, flexShrink: 0 }}>
                <Typography variant="subtitle2" sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'text.secondary', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Templates
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Search…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Iconify icon="eva:search-outline" width={16} sx={{ color: 'text.disabled' }} />
                                </InputAdornment>
                            ),
                            sx: { borderRadius: 1.5, fontSize: '0.8rem', bgcolor: 'background.paper' },
                        }}
                    />
                    <Tooltip title={`Sort · ${SORT_LABELS[sortOrder]}`}>
                        <IconButton
                            size="small"
                            data-testid="template-sort-select"
                            onClick={(e) => setSortMenuAnchor(e.currentTarget)}
                            sx={{ flexShrink: 0, color: sortOrder !== 'recent' ? 'primary.main' : 'text.secondary', '&:hover': { color: 'primary.main' } }}
                        >
                            <Iconify icon="mdi:sort-variant" width={16} />
                        </IconButton>
                    </Tooltip>
                    <Menu
                        anchorEl={sortMenuAnchor}
                        open={Boolean(sortMenuAnchor)}
                        onClose={() => setSortMenuAnchor(null)}
                        PaperProps={{ sx: { minWidth: 148, borderRadius: 1.5, boxShadow: '0 4px 16px rgba(0,0,0,0.10)' } }}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        {SORT_OPTIONS.map(([value, label]) => (
                            <MenuItem
                                key={value}
                                selected={sortOrder === value}
                                onClick={() => { setSortOrder(value); setSortMenuAnchor(null); }}
                                sx={{ fontSize: '12px', gap: 1, py: 0.75 }}
                            >
                                <Box sx={{ width: 14, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                    {sortOrder === value && <Iconify icon="eva:checkmark-fill" width={14} sx={{ color: 'primary.main' }} />}
                                </Box>
                                {label}
                            </MenuItem>
                        ))}
                    </Menu>
                </Box>
            </Box>

            {/* ── Category chip strip with scroll arrows (only when 2+ categories present) ── */}
            {categories.length > 1 && (
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, px: 0.5, pb: 0.75, pt: 0.25, gap: 0 }}>
                    {/* Left scroll arrow */}
                    <IconButton
                        size="small"
                        data-testid="chip-scroll-left"
                        onClick={() => chipStripRef.current?.scrollBy({ left: -200, behavior: 'smooth' })}
                        disabled={!canScrollLeft}
                        sx={{
                            p: '2px',
                            flexShrink: 0,
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            opacity: canScrollLeft ? 1 : 0.35,
                            cursor: canScrollLeft ? 'pointer' : 'default',
                            '&:hover': { bgcolor: canScrollLeft ? 'rgba(0,0,0,0.04)' : 'transparent' },
                        }}
                    >
                        <Iconify icon="eva:chevron-left-fill" width={14} />
                    </IconButton>

                    {/* Scrollable chip strip */}
                    <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
                        <Box
                            ref={chipStripRef}
                            data-testid="category-chip-strip"
                            sx={{
                                display: 'flex',
                                gap: 0.75,
                                overflowX: 'auto',
                                scrollbarWidth: 'none',
                                '&::-webkit-scrollbar': { display: 'none' },
                                scrollSnapType: 'x mandatory',
                            }}
                        >
                            {categories.map((cat) => {
                                const isActive = selectedCategory === cat;
                                return (
                                    <Box
                                        key={cat}
                                        component="button"
                                        data-testid={`category-chip-${cat}`}
                                        onClick={() => setSelectedCategory(cat)}
                                        sx={{
                                            flexShrink: 0,
                                            height: '24px',
                                            px: 1.25,
                                            borderRadius: '20px',
                                            fontSize: '11px',
                                            fontWeight: 500,
                                            border: 'none',
                                            cursor: 'pointer',
                                            transition: 'all 0.12s',
                                            bgcolor: isActive ? '#0071e3' : alpha('#0071e3', 0.08),
                                            color: isActive ? '#fff' : '#0071e3',
                                            scrollSnapAlign: 'start',
                                            '&:hover': { opacity: 0.88 },
                                        }}
                                    >
                                        {cat}
                                    </Box>
                                );
                            })}
                        </Box>
                        {/* Subtle right-edge fade (8px) — visible only when more chips to scroll */}
                        {canScrollRight && (
                            <Box sx={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                bottom: 0,
                                width: 8,
                                background: (theme) => `linear-gradient(to right, transparent, ${alpha(theme.palette.grey[100], 0.90)})`,
                                pointerEvents: 'none',
                            }} />
                        )}
                    </Box>

                    {/* Right scroll arrow */}
                    <IconButton
                        size="small"
                        data-testid="chip-scroll-right"
                        onClick={() => chipStripRef.current?.scrollBy({ left: 200, behavior: 'smooth' })}
                        disabled={!canScrollRight}
                        sx={{
                            p: '2px',
                            flexShrink: 0,
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            opacity: canScrollRight ? 1 : 0.35,
                            cursor: canScrollRight ? 'pointer' : 'default',
                            '&:hover': { bgcolor: canScrollRight ? 'rgba(0,0,0,0.04)' : 'transparent' },
                        }}
                    >
                        <Iconify icon="eva:chevron-right-fill" width={14} />
                    </IconButton>

                    {/* Dots overflow button — quick "show all" when >8 categories */}
                    {categories.length > CAT_OVERFLOW_THRESHOLD + 1 && (
                        <>
                            <Tooltip title="All categories">
                                <IconButton
                                    size="small"
                                    onClick={(e) => setCatOverflowAnchor(e.currentTarget)}
                                    sx={{ p: '2px', flexShrink: 0, width: 24, height: 24, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                                >
                                    <Iconify icon="mdi:dots-horizontal" width={14} />
                                </IconButton>
                            </Tooltip>
                            <Popover
                                open={Boolean(catOverflowAnchor)}
                                anchorEl={catOverflowAnchor}
                                onClose={() => setCatOverflowAnchor(null)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                PaperProps={{ sx: { borderRadius: 1.5, p: 0.75, minWidth: 160, boxShadow: '0 4px 16px rgba(0,0,0,0.10)' } }}
                            >
                                {categories.map((cat) => {
                                    const isActive = selectedCategory === cat;
                                    return (
                                        <Box
                                            key={cat}
                                            component="button"
                                            onClick={() => { setSelectedCategory(cat); setCatOverflowAnchor(null); }}
                                            sx={{
                                                display: 'block',
                                                width: '100%',
                                                textAlign: 'left',
                                                border: 'none',
                                                bgcolor: 'transparent',
                                                cursor: 'pointer',
                                                px: 1.25,
                                                py: 0.5,
                                                borderRadius: 1,
                                                fontSize: '12px',
                                                fontWeight: isActive ? 600 : 400,
                                                color: isActive ? 'primary.main' : 'text.primary',
                                                '&:hover': { bgcolor: 'action.hover' },
                                            }}
                                        >
                                            {cat}
                                        </Box>
                                    );
                                })}
                            </Popover>
                        </>
                    )}
                </Box>
            )}

            {/* ── Scrollable body ── */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 1, pb: 1 }}>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
                        <CircularProgress size={22} />
                    </Box>
                )}

                {/* Recently used compact horizontal row */}
                {!loading && !isFiltered && recentTemplates.length > 0 && (
                    <Box sx={{ mb: 1.5 }} data-testid="recently-used-section">
                        <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#86868b', textTransform: 'uppercase', letterSpacing: '0.06em', px: 0.5, mb: 0.5 }}>
                            Recently used
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
                            {recentTemplates.map((t) => {
                                const isSelected = selectedId === t.id;
                                return (
                                    <Box
                                        key={t.id}
                                        onClick={() => onSelect(t)}
                                        sx={{
                                            flexShrink: 0,
                                            width: 140,
                                            p: '8px 10px',
                                            borderRadius: '8px',
                                            border: '1px solid',
                                            borderColor: isSelected ? 'primary.main' : 'rgba(145,158,171,0.14)',
                                            bgcolor: isSelected ? (theme) => alpha(theme.palette.primary.main, 0.06) : 'background.paper',
                                            cursor: 'pointer',
                                            transition: 'all 0.12s',
                                            '&:hover': { borderColor: 'rgba(145,158,171,0.30)', transform: 'translateY(-1px)', boxShadow: '0 2px 4px rgba(0,0,0,0.06)' },
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {t.subject || t.name}
                                        </Typography>
                                        {t.category && (
                                            <Typography sx={{ fontSize: '10px', color: 'text.disabled', mt: '2px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                                                {(t.category || '').replace(/_/g, ' ')}
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                )}

                {!loading && displayedTemplates.length === 0 && (
                    <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', pt: 3, color: 'text.disabled' }}>
                        No templates found
                    </Typography>
                )}
                <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                    {displayedTemplates.map((t) => renderCard(t))}
                </Stack>
            </Box>
        </Box>
    );
}
EmailTemplateSidePanel.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onSelect: PropTypes.func,
    selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

// -----------------------------------------------
// AttachmentRow — file chips + upload trigger
// -----------------------------------------------
function AttachmentRow({ attachments, onAdd, onRemove }) {
    const fileRef = useRef();
    return (
        <Box sx={{ px: 3, py: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', minHeight: 36 }}>
            {attachments.map((a, i) => (
                <Chip
                    key={i}
                    label={a.name || `Attachment ${i + 1}`}
                    size="small"
                    onDelete={() => onRemove(i)}
                    sx={{ fontSize: '0.72rem', height: 24 }}
                />
            ))}
            <Tooltip title="Attach file">
                <IconButton
                    size="small"
                    onClick={() => fileRef.current?.click()}
                    sx={{ p: 0.5, color: 'text.disabled', '&:hover': { color: 'primary.main' } }}
                >
                    <Iconify icon="eva:attach-2-outline" width={16} />
                </IconButton>
            </Tooltip>
            <input
                ref={fileRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => { if (e.target.files) onAdd(Array.from(e.target.files)); e.target.value = ''; }}
            />
        </Box>
    );
}
AttachmentRow.propTypes = {
    attachments: PropTypes.array,
    onAdd: PropTypes.func,
    onRemove: PropTypes.func,
};

// -----------------------------------------------
// ScheduleSendPopover
// -----------------------------------------------
function ScheduleSendPopover({ anchorEl, onClose, onSchedule }) {
    const [dt, setDt] = useState(moment().add(1, 'hour'));
    return (
        <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            PaperProps={{ sx: { p: 2, borderRadius: 2, width: 280 } }}
        >
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontSize: '0.82rem' }}>Schedule send</Typography>
            <LocalizationProvider dateAdapter={AdapterMoment}>
                <DateTimePicker
                    label="Send at"
                    value={dt}
                    onChange={(v) => setDt(v)}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    minDateTime={moment()}
                />
            </LocalizationProvider>
            <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 1.5 }}>
                <Button size="small" color="inherit" onClick={onClose}>Cancel</Button>
                <Button
                    size="small"
                    variant="contained"
                    disabled={!dt || !moment.isMoment(moment(dt)) || !moment(dt).isValid() || moment(dt).isBefore(moment())}
                    onClick={() => { onSchedule(dt); onClose(); }}
                >
                    Schedule
                </Button>
            </Stack>
        </Popover>
    );
}
ScheduleSendPopover.propTypes = {
    anchorEl: PropTypes.object,
    onClose: PropTypes.func,
    onSchedule: PropTypes.func,
};

// -----------------------------------------------
// IosEmailComposerBody — the compose UI (no Dialog wrapper)
// -----------------------------------------------
export default function IosEmailComposerBody({ leadId, templateId, practiceId, onSendSuccess, leadDetail, onDismiss, compact }) {
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [ccOpen, setCcOpen] = useState(false);
    const [cc, setCc] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [sending, setSending] = useState(false);
    const [scheduleAnchor, setScheduleAnchor] = useState(null);
    const [scheduledAt, setScheduledAt] = useState(null);
    const [templateName, setTemplateName] = useState(null);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [templateDrawerOpen, setTemplateDrawerOpen] = useState(false);
    const [emailAdded, setEmailAdded] = useState('');
    const quillRef = useRef(null);

    const toEmail = emailAdded
        || leadDetail?.email
        || leadDetail?.lead_contact?.email
        || leadDetail?.originalData?.lead_contact?.email
        || '';

    const toName = [leadDetail?.first_name, leadDetail?.last_name].filter(Boolean).join(' ') || toEmail;

    // Pre-fill from templateId when it changes
    useEffect(() => {
        if (!templateId || !practiceId) return;
        callApi(`/api/v1/practices/${practiceId}/engagement-templates/${templateId}/`)
            .then((res) => {
                if (res.status === 200) {
                    setSubject(res.data.subject || '');
                    setBody(res.data.body || '');
                    setTemplateName(res.data.name || null);
                } else {
                    dispatch(updateError({ success: false, message: 'Could not load template — please retry' }));
                }
            })
            .catch((err) => {
                console.error('IosEmailComposerBody: template fetch failed', err);
                dispatch(updateError({ success: false, message: 'Could not load template — please retry' }));
            });
    }, [templateId, practiceId]);

    // Reset form and notify parent (dismiss/close)
    const handleClose = useCallback(() => {
        setSubject('');
        setBody('');
        setCc('');
        setCcOpen(false);
        setAttachments([]);
        setSending(false);
        setScheduledAt(null);
        setTemplateName(null);
        setSelectedTemplateId(null);
        setTemplateDrawerOpen(false);
        onDismiss();
    }, [onDismiss]);

    const handleTemplateSelect = (t) => {
        setSubject(t.subject || '');
        setBody(t.body || '');
        setTemplateName(t.name || null);
        setSelectedTemplateId(t.id);
        setTemplateDrawerOpen(false);
    };

    const handleAddAttachments = (files) => {
        setAttachments((prev) => [...prev, ...files]);
    };

    const handleRemoveAttachment = (idx) => {
        setAttachments((prev) => prev.filter((_, i) => i !== idx));
    };

    const buildAttachmentPayload = () =>
        Promise.all(
            attachments.map((file) =>
                new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve({ file: reader.result, name: file.name });
                    reader.readAsDataURL(file);
                })
            )
        );

    const handleSend = async (scheduleTime) => {
        if (!subject.trim()) {
            dispatch(updateError({ success: false, message: 'Subject is required' }));
            return;
        }
        const bodyContent = body.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
        if (!bodyContent) {
            dispatch(updateError({ success: false, message: 'Email body cannot be empty' }));
            return;
        }
        setSending(true);
        try {
            const attachmentPayload = await buildAttachmentPayload();
            const modifiedBody = applyStylesToTags(body);
            const detail = {
                lead: leadId,
                channel: 'EMAIL',
                subject: subject.trim(),
                body: modifiedBody,
                attachments: attachmentPayload,
            };
            if (cc.trim()) detail.cc = [cc.trim()];
            if (scheduleTime) detail.send_at = scheduleTime.toISOString();

            const res = await callApi(
                `/api/v1/practices/${practiceId}/transactional-messages/`,
                'post',
                detail
            );

            if (res.status === 201) {
                dispatch(updateError({ success: true, message: 'Email sent successfully' }));
                const messageId = res.data?.id || null;
                if (selectedTemplateId) {
                    addRecentTemplate(practiceId, selectedTemplateId);
                    incrementUsageCount(practiceId, selectedTemplateId);
                }
                handleClose();
                if (onSendSuccess) onSendSuccess(messageId);
            } else {
                // Extract a human-readable message — avoid dumping raw JSON to the user
                const errMsg =
                    res.data?.detail ||
                    res.data?.message ||
                    res.data?.non_field_errors?.[0] ||
                    (typeof res.data === 'string' ? res.data : null) ||
                    'Failed to send email — please try again';
                dispatch(updateError({ success: false, message: errMsg }));
            }
        } catch {
            dispatch(updateError({ success: false, message: 'Failed to send email — please try again' }));
        } finally {
            setSending(false);
        }
    };

    const handleSchedule = (dt) => {
        setScheduledAt(dt);
        handleSend(dt);
    };

    // Send Now always sends immediately regardless of any previously-set schedule.
    // We clear scheduledAt first so the UI indicator resets before the API call.
    const handleSendNow = () => {
        setScheduledAt(null);
        handleSend(null);
    };

    // Strip HTML tags to check whether the Quill body has actual text content
    const bodyText = body.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    const canSend = subject.trim().length > 0 && bodyText.length > 0 && !sending;
    // compact=true forces single-view (template in drawer) regardless of viewport width.
    // This is needed when hosted in a narrow sidebar column where side-by-side doesn't fit.
    const viewportIsWide = useMediaQuery('(min-width:900px)');
    const isWide = !compact && viewportIsWide;

    // ── Compose pane content ──
    const composePaneContent = (
        <>
            {/* ── Header ── */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 1.5,
                    pt: 1,
                    pb: 0.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    flexShrink: 0,
                }}
            >
                <Button
                    size="small"
                    onClick={handleClose}
                    sx={{ fontSize: '0.875rem', color: 'primary.main', textTransform: 'none', fontWeight: 400, minWidth: 0, px: 1 }}
                >
                    Cancel
                </Button>

                <Box sx={{ flex: 1, textAlign: 'center' }}>
                    <Typography variant="subtitle2" sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
                        New Message
                    </Typography>
                    {templateName && (
                        <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary', display: 'block', lineHeight: 1.2 }}>
                            {templateName}
                        </Typography>
                    )}
                </Box>

                <Tooltip title={canSend ? 'Send now' : (!subject.trim() ? 'Subject required' : 'Body required')}>
                    <span>
                        <IconButton
                            data-testid="email-composer-send-btn"
                            onClick={handleSendNow}
                            disabled={!canSend}
                            sx={{
                                bgcolor: canSend ? 'primary.main' : 'action.disabledBackground',
                                color: canSend ? '#fff' : 'text.disabled',
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                '&:hover': { bgcolor: 'primary.dark' },
                                transition: 'background-color 200ms ease',
                            }}
                        >
                            {sending
                                ? <CircularProgress size={14} sx={{ color: '#fff' }} />
                                : <Iconify icon="eva:paper-plane-fill" width={15} />
                            }
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            {/* ── Field hierarchy ── */}
            <Box sx={{ overflowY: 'auto', flex: 1 }}>
                {/* To */}
                <FieldRow label="To">
                    {toEmail ? (
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.primary' }}>
                            {`${toName} <${toEmail}>`}
                        </Typography>
                    ) : (
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.disabled' }}>
                                No email on file
                            </Typography>
                            <InlineEmailAddField
                                practiceId={practiceId}
                                leadId={leadId}
                                onSuccess={(email) => setEmailAdded(email)}
                            />
                        </Box>
                    )}
                </FieldRow>

                {/* Cc/Bcc toggle */}
                <FieldRow label={ccOpen ? 'Cc' : ''}>
                    {ccOpen ? (
                        <BareInput
                            value={cc}
                            onChange={setCc}
                            placeholder="cc@example.com"
                            inputProps={{ maxLength: 254, type: 'email' }}
                        />
                    ) : (
                        <Button
                            size="small"
                            onClick={() => setCcOpen(true)}
                            sx={{ fontSize: '0.78rem', color: 'text.secondary', textTransform: 'none', p: 0, minWidth: 0, '&:hover': { color: 'primary.main', bgcolor: 'transparent' } }}
                        >
                            Cc/Bcc
                        </Button>
                    )}
                </FieldRow>

                {/* Subject */}
                <FieldRow label="Subject">
                    <BareInput
                        value={subject}
                        onChange={setSubject}
                        placeholder="Subject"
                        inputProps={{ maxLength: 255 }}
                    />
                </FieldRow>

                {/* Attachments */}
                {attachments.length > 0 && (
                    <>
                        <Divider sx={{ mx: 3, borderColor: 'divider', opacity: 0.6 }} />
                        <AttachmentRow
                            attachments={attachments}
                            onAdd={handleAddAttachments}
                            onRemove={handleRemoveAttachment}
                        />
                    </>
                )}

                {/* Body — ReactQuill */}
                <Box
                    sx={{
                        px: 3,
                        pt: 1,
                        pb: 2,
                        minHeight: 220,
                        '& .ql-toolbar': {
                            border: 'none',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            px: 0,
                        },
                        '& .ql-container': {
                            border: 'none',
                            fontSize: '0.875rem',
                            fontFamily: 'inherit',
                        },
                        '& .ql-editor': {
                            minHeight: 200,
                            p: '8px 0',
                            '&.ql-blank::before': { fontStyle: 'normal', color: 'rgba(145,158,171,0.8)', left: 0 },
                        },
                    }}
                >
                    <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={body}
                        onChange={setBody}
                        modules={QUILL_MODULES}
                        formats={QUILL_FORMATS}
                        placeholder="Write your message…"
                    />
                </Box>
            </Box>

            {/* ── Bottom toolbar ── */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    py: 0.75,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    bgcolor: (theme) => alpha(theme.palette.grey[100], 0.6),
                    flexShrink: 0,
                    gap: 0.5,
                }}
            >
                {!isWide && (
                    <Tooltip title="Browse templates">
                        <IconButton
                            size="small"
                            onClick={() => setTemplateDrawerOpen(true)}
                            sx={{ color: selectedTemplateId ? 'primary.main' : 'text.secondary', '&:hover': { color: 'primary.main' } }}
                        >
                            <Iconify icon="eva:file-text-outline" width={18} />
                        </IconButton>
                    </Tooltip>
                )}

                <Tooltip title="Attach file">
                    <IconButton
                        size="small"
                        onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.multiple = true;
                            input.onchange = (e) => handleAddAttachments(Array.from(e.target.files));
                            input.click();
                        }}
                        sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                    >
                        <Iconify icon="eva:attach-2-outline" width={18} />
                    </IconButton>
                </Tooltip>

                <Tooltip title={scheduledAt ? `Scheduled: ${moment(scheduledAt).format('MMM D, h:mm a')}` : 'Schedule send'}>
                    <IconButton
                        size="small"
                        onClick={(e) => setScheduleAnchor(e.currentTarget)}
                        sx={{ color: scheduledAt ? 'primary.main' : 'text.secondary', '&:hover': { color: 'primary.main' } }}
                    >
                        <Iconify icon="eva:clock-outline" width={18} />
                    </IconButton>
                </Tooltip>

                <Box sx={{ flex: 1 }} />

                <Tooltip title="Discard">
                    <IconButton
                        size="small"
                        onClick={handleClose}
                        sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                    >
                        <Iconify icon="eva:trash-2-outline" width={18} />
                    </IconButton>
                </Tooltip>
            </Box>
        </>
    );

    return (
        <>
            {/* ── Outer container with template side panel for wide viewports ── */}
            <Box
                data-testid="ios-email-composer-body"
                sx={{ display: 'flex', flexDirection: 'row', height: '100%', overflow: 'hidden' }}
            >
                {isWide && (
                    <EmailTemplateSidePanel
                        practiceId={practiceId}
                        onSelect={handleTemplateSelect}
                        selectedId={selectedTemplateId}
                    />
                )}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
                    {composePaneContent}
                </Box>
            </Box>

            {/* ── Narrow-viewport template drawer ── */}
            <Drawer
                anchor="bottom"
                open={templateDrawerOpen}
                onClose={() => setTemplateDrawerOpen(false)}
                PaperProps={{
                    sx: {
                        borderRadius: '16px 16px 0 0',
                        maxHeight: '75vh',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', px: 2, pt: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                    <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 600 }}>Email Templates</Typography>
                    <IconButton size="small" onClick={() => setTemplateDrawerOpen(false)}>
                        <Iconify icon="eva:close-fill" width={20} />
                    </IconButton>
                </Box>
                <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <EmailTemplateSidePanel
                        practiceId={practiceId}
                        onSelect={handleTemplateSelect}
                        selectedId={selectedTemplateId}
                    />
                </Box>
            </Drawer>

            <ScheduleSendPopover
                anchorEl={scheduleAnchor}
                onClose={() => setScheduleAnchor(null)}
                onSchedule={handleSchedule}
            />
        </>
    );
}

IosEmailComposerBody.propTypes = {
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    templateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onSendSuccess: PropTypes.func,
    leadDetail: PropTypes.object,
    onDismiss: PropTypes.func,
    /** When true: forces single-view layout (template picker in drawer), ignoring viewport width.
     *  Use when the component is hosted in a narrow container like a sidebar tab. */
    compact: PropTypes.bool,
};

IosEmailComposerBody.defaultProps = {
    leadId: null,
    templateId: null,
    practiceId: null,
    onSendSuccess: null,
    leadDetail: null,
    onDismiss: () => {},
    compact: false,
};
